import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./add-new-fields.module.scss";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import type { GetLayoutFieldType } from "@whatslly/types/GetLayoutOptionsResponse";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import classnames from "classnames";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { Separator } from "@whatslly/ui-components/Separator";
import { Button } from "@whatslly/ui-components/v2/Button";
import type { LayoutFieldType } from "@whatslly/types/LayoutFieldType";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { TextToolTipCheck } from "@whatslly/ui-components/TextToolTipCheck";

const NewField: React.FC<{
  field: GetLayoutFieldType;
  updateSelectedFields: (isChecked: boolean, field: GetLayoutFieldType) => void;
  fieldIsSelected: boolean;
  reachedMaxFields: boolean;
  maxFields: number;
}> = ({ field, updateSelectedFields, fieldIsSelected, reachedMaxFields, maxFields }) => {
  const [fieldChecked, setFieldChecked] = useState<boolean>(fieldIsSelected);
  const { t } = useTranslation();

  const addFieldToArray = (isChecked: boolean, newField: GetLayoutFieldType) => {
    setFieldChecked(isChecked);
    updateSelectedFields(isChecked, newField);
  };

  const showCheckboxTooltip = () => {
    if (reachedMaxFields) {
      return fieldChecked;
    }
    return true;
  };

  return (
    <div className={styles.newFieldContainer}>
      <Tooltip
        className={styles.tooltip}
        value={t("editLayouts.you_can_only_select_up to_fields", { numberOfFields: maxFields })}
        disabled={showCheckboxTooltip()}
      >
        <div className={styles.newFieldCheckBoxContainer}>
          <CheckBox
            onChange={(e) => addFieldToArray(e.target.checked, field)}
            checked={fieldChecked}
            disabled={!fieldChecked && reachedMaxFields}
            className={classnames(styles.fieldCheckBox, {
              [globalStyles["dark-mode-bg-color-brand-primary"]]: reachedMaxFields,
              [globalStyles["dark-mode-bg-color-none"]]: !reachedMaxFields,
              [globalStyles["dark-mode-remove-border"]]: reachedMaxFields,
              [globalStyles["bc-neutral-secondary"]]: !reachedMaxFields,
            })}
            textColor={`neutral-primary`}
            clickAbleLabel
            checkBoxTextGap={17}
          />
        </div>
      </Tooltip>
      <TextToolTipCheck
        toolTipClassName={styles.tooltip}
        className={classnames(styles.newFieldTitle, { [styles.disabled]: !fieldChecked && reachedMaxFields })}
        text={field.label}
      />
      <Tooltip delay={500} value={field.name} className={styles.tooltip}>
        <div className={classnames(styles.newFieldType, { [styles.disabled]: !fieldChecked && reachedMaxFields })}>
          {field.type.charAt(0).toUpperCase() + field.type.slice(1)}
        </div>
      </Tooltip>
    </div>
  );
};

export const AddNewFields: React.FC<{
  close: (fieldsToReturn: Record<string, (GetLayoutFieldType | LayoutFieldType | undefined)[]> | undefined) => void;
  allFields: GetLayoutFieldType[];
  initialSelectedFields: LayoutFieldType[];
  maxFields: number;
}> = ({ close, initialSelectedFields, allFields, maxFields }) => {
  const [initialSelectedFieldsOrganized] = useState<GetLayoutFieldType[]>(() =>
    initialSelectedFields.map((field) => {
      return { label: field.l, name: field.a, type: field.t };
    })
  );
  const [newSelectedFields, setNewSelectedFields] = useState<GetLayoutFieldType[]>(initialSelectedFieldsOrganized);
  const [fields, setFields] = useState(allFields);
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation();

  const reachedMaxFields = useMemo(() => newSelectedFields.length >= maxFields, [maxFields, newSelectedFields.length]);

  const filterSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length === 0) {
      setFields(allFields);
      return;
    }
    if (query.length === 1) {
      return;
    }
    const res = allFields.filter((filter) => filter.label.toLowerCase().includes(query.toLowerCase()));
    setFields(res);
  };

  const updateSelectedFields = (isChecked: boolean, newField: GetLayoutFieldType) => {
    if (!isChecked) {
      const filteredNewSelectedFields = newSelectedFields.filter((e) => e.name !== newField.name);
      setNewSelectedFields(filteredNewSelectedFields);
      return;
    }
    setNewSelectedFields([...newSelectedFields, newField]);
  };

  const prepareFieldsToDelete = () => {
    const res = initialSelectedFields.map((initialField) => {
      const findObj = newSelectedFields.find((newField) => newField.name === initialField.a);
      if (!findObj) {
        return initialField;
      }
    });
    return res.filter(Boolean);
  };

  const prepareFieldsToAdd = () => {
    const res = newSelectedFields.map((newField) => {
      if (!initialSelectedFieldsOrganized.includes(newField)) {
        return newField;
      }
    });
    return res.filter(Boolean);
  };

  const onSubmit = () => {
    const arrayToReturn: Record<string, (GetLayoutFieldType | LayoutFieldType | undefined)[]> = {
      fieldsToDelete: prepareFieldsToDelete(),
      fieldsToAdd: prepareFieldsToAdd(),
    };
    if (arrayToReturn.fieldsToDelete && arrayToReturn.fieldsToAdd) {
      close(arrayToReturn);
    }
  };

  return (
    <div className={styles.addNewFieldsModal}>
      <div className={styles.addNewFieldsHeader}>
        <div className={styles.addNewFieldsTitleAndClose}>
          <div className={styles.addNewFieldsTitle}>{t("helpers_add_new_field")}</div>
        </div>
        <SearchInput onChange={(e) => filterSearch(e)} placeholder={t("editLayouts.search_field_name")} value={searchQuery} />
        <Separator className={styles.tabsSeparator} />
        <div className={styles.fieldLimitTitle}>{t("editLayouts.select_up_to_display_fields", { numberOfFields: maxFields })}</div>
      </div>

      <div className={styles.addFieldsContainer}>
        {fields?.map((field, index) => {
          const isFieldSelected = newSelectedFields.find((item) => item.name === field.name);
          return (
            <NewField
              key={field.name + index}
              field={field}
              updateSelectedFields={updateSelectedFields}
              fieldIsSelected={!!isFieldSelected}
              reachedMaxFields={reachedMaxFields}
              maxFields={maxFields}
            />
          );
        })}
      </div>
      <Separator className={styles.tabsSeparator} />
      <footer className={styles.footer}>
        <Button onClick={() => close(undefined)} className={styles.cancelButton}>
          {t("opp_form_fields_modal.cancel")}
        </Button>
        <Button onClick={() => onSubmit()} className={styles.addButton}>
          {`${t("editLayouts.add_fields")}`}
          <span>{newSelectedFields.length > 0 && ` (${newSelectedFields.length})`}</span>
        </Button>
      </footer>
    </div>
  );
};
